import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Wrench,
  ClipboardList,
  Layers,
  Send,
  Play,
  CheckCircle,
  CheckCircle2,
  Archive,
  RefreshCw,
  Loader2,
  Building2,
  Users,
  Calendar,
  AlertTriangle,
  Clock,
  Eye,
  FileText,
  X,
  Settings,
  Mail,
  Plus,
  CalendarClock,
  Edit,
  Trash2,
  MessageSquare,
  History,
  Save,
  ChevronRight,
  ChevronLeft,
  ArrowRight,
  GripVertical,
  Bell,
  Camera,
  Upload,
  Image,
  ExternalLink,
  UserPlus,
  Zap,
  RefreshCcw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import EntityDetailModal from "@/components/admin/EntityDetailModal";
import SendUpdateEmailModal from "@/components/admin/SendUpdateEmailModal";
import TicketChat from "@/components/TicketChat";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STAGES = [
  { key: "intake", label: "Intake", icon: ClipboardList, color: "bg-slate-500" },
  { key: "triage", label: "Triage", icon: Layers, color: "bg-amber-500" },
  { key: "dispatch", label: "Dispatch", icon: Send, color: "bg-blue-500" },
  { key: "in_progress", label: "In Progress", icon: Play, color: "bg-purple-500" },
  { key: "completion", label: "Completion", icon: CheckCircle, color: "bg-emerald-500" },
  { key: "closed", label: "Closed", icon: Archive, color: "bg-slate-600" }
];

// Draggable Card Component for pipeline items
function DraggableCard({ item, children, isSuperAdmin }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: item.id,
    disabled: !isSuperAdmin 
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative ${isDragging ? 'shadow-lg ring-2 ring-blue-400' : ''}`}
    >
      {isSuperAdmin && (
        <div
          {...attributes}
          {...listeners}
          className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center cursor-grab active:cursor-grabbing bg-blue-50 hover:bg-blue-100 rounded-l-lg border-r border-blue-200 z-20 touch-none"
          onClick={(e) => e.stopPropagation()}
          title="Drag to move"
        >
          <GripVertical className="w-5 h-5 text-blue-500" />
        </div>
      )}
      <div className={isSuperAdmin ? "pl-8" : ""}>
        {children}
      </div>
    </div>
  );
}

// Droppable Stage Column
function DroppableStage({ stageKey, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: stageKey,
    data: { type: 'stage', stageKey }
  });

  return (
    <div 
      ref={setNodeRef}
      data-stage={stageKey}
      className={`bg-slate-100 rounded-b-lg p-2 min-h-[400px] space-y-2 transition-colors ${
        isOver ? 'bg-blue-100 ring-2 ring-blue-300' : ''
      }`}
    >
      {children}
    </div>
  );
}

export default function MaintenanceManagement() {
  const { token, user } = useAuth();
  const [pipeline, setPipeline] = useState({});
  const [counts, setCounts] = useState({});
  const [contractors, setContractors] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("pipeline");
  const [selectedItem, setSelectedItem] = useState(null);
  const [showWorkOrderModal, setShowWorkOrderModal] = useState(false);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEntityDetailModal, setShowEntityDetailModal] = useState(false);
  const [entityDetailId, setEntityDetailId] = useState(null);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [sendEmailEntityId, setSendEmailEntityId] = useState(null);
  const [sendEmailEntityName, setSendEmailEntityName] = useState(null);
  const [showCreateInternalModal, setShowCreateInternalModal] = useState(false);
  const [showAddFacilityModal, setShowAddFacilityModal] = useState(false);
  const [showManageAssignmentsModal, setShowManageAssignmentsModal] = useState(false);
  const [manageAssignmentsItem, setManageAssignmentsItem] = useState(null);
  const [addMemberEmail, setAddMemberEmail] = useState("");
  const [addingMember, setAddingMember] = useState(false);
  const [healthSystems, setHealthSystems] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [assets, setAssets] = useState([]);
  const [newFacilityForm, setNewFacilityForm] = useState({
    name: "",
    facility_type: "hospital",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    phone: ""
  });

  // Edit, Delete, Notes, Audit states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showAuditLogModal, setShowAuditLogModal] = useState(false);
  const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
  const [updateItem, setUpdateItem] = useState(null);
  const [updateMessage, setUpdateMessage] = useState("");
  const [updateStatus, setUpdateStatus] = useState("");
  const [submittingUpdate, setSubmittingUpdate] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [deletingItems, setDeletingItems] = useState([]); // For bulk delete
  const [selectedForDelete, setSelectedForDelete] = useState(new Set()); // Track selected items
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [activeId, setActiveId] = useState(null);
  
  // Contractor Sync states
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncingContractor, setSyncingContractor] = useState(false);
  const [syncContractorEmail, setSyncContractorEmail] = useState("");
  const [syncPriority, setSyncPriority] = useState("normal");
  const [syncStatus, setSyncStatus] = useState(null);
  
  const [editForm, setEditForm] = useState({
    request_type: "",
    priority: "",
    description: "",
    stage: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    purchase_order_number: "",
    invoice_number: "",
    additional_contacts: [] // [{name, email, role}]
  });

  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const isSuperAdmin = user?.role === "super_admin";

  // DnD Sensors - support for both mouse and touch
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );
  
  const [workOrderForm, setWorkOrderForm] = useState({
    scope_of_work: "",
    estimated_hours: "",
    estimated_cost: "",
    special_instructions: "",
    priority: "normal",
    terms: "Net 30",
    due_date: "",
    assigned_contractors: [],
    assigned_suppliers: [],
    additional_contacts: [],
    send_notifications: true
  });
  
  // Additional contact form for work orders
  const [woNewContact, setWoNewContact] = useState({ name: "", email: "", phone: "" });
  
  // Live On-Site Contractors tracking
  const [liveOnSiteContractors, setLiveOnSiteContractors] = useState([]);
  const [loadingLiveTracking, setLoadingLiveTracking] = useState(false);
  
  const [dispatchForm, setDispatchForm] = useState({
    contractor_id: "",
    dispatch_notes: "",
    expected_arrival: ""
  });

  const [internalRequestForm, setInternalRequestForm] = useState({
    hospital_id: "",
    hospital_name: "",
    facility_id: "",
    facility_name: "",
    request_type: "preventive_maintenance",
    priority: "normal",
    description: "",
    asset_id: "",
    asset_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    scheduled_date: "",
    notes: "",
    assigned_contractor_id: "",
    assigned_contractor_name: "",
    notify_users: [], // Array of user IDs to notify
    photos: [], // Array of photo URLs
    additional_contacts: [] // Array of {name, email, phone, role} for multi-contact support
  });
  
  // Additional contact input for multi-contact form
  const [newContact, setNewContact] = useState({ name: "", email: "", phone: "", role: "hospital_contact" });
  
  const [hospitalUsersForNotify, setHospitalUsersForNotify] = useState([]);
  const [loadingHospitalUsers, setLoadingHospitalUsers] = useState(false);
  const [uploadingInternalPhoto, setUploadingInternalPhoto] = useState(false);
  const internalPhotoInputRef = React.useRef(null);
  const internalCameraInputRef = React.useRef(null);

  // Fetch hospital users when hospital is selected
  const fetchHospitalUsersForNotification = async (hospitalId) => {
    if (!hospitalId) {
      setHospitalUsersForNotify([]);
      return;
    }
    setLoadingHospitalUsers(true);
    try {
      const res = await fetch(`${API}/admin/health-systems/${hospitalId}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHospitalUsersForNotify(data.users || []);
      }
    } catch (err) {
      console.error("Failed to fetch hospital users:", err);
    } finally {
      setLoadingHospitalUsers(false);
    }
  };

  const fetchData = async () => {
    console.log("Refresh button clicked - fetching data...");
    setLoading(true);
    try {
      const pipelineRes = await fetch(`${API}/maintenance/pipeline`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      console.log("Pipeline response status:", pipelineRes.status);
      
      if (pipelineRes.ok) {
        const data = await pipelineRes.json();
        console.log("Pipeline data received:", Object.keys(data));
        // API returns both 'pipeline' and 'stages' - use either
        const newPipeline = data.stages || data.pipeline || {};
        setPipeline(newPipeline);
        setCounts(data.counts || {});
        console.log("Pipeline updated with stages:", Object.keys(newPipeline));
      } else {
        console.error("Pipeline fetch failed:", pipelineRes.status);
        toast.error(`Failed to fetch pipeline: ${pipelineRes.status}`);
      }
      
      // Fetch contractors
      const contractorsRes = await fetch(`${API}/contractors/profiles`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      if (contractorsRes.ok) {
        const data = await contractorsRes.json();
        setContractors(data.contractors || []);
      }
      
      // Fetch suppliers
      const suppliersRes = await fetch(`${API}/suppliers`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      if (suppliersRes.ok) {
        const data = await suppliersRes.json();
        setSuppliers(data.suppliers || data || []);
      }
      
      // Fetch health systems
      const healthSystemsRes = await fetch(`${API}/health-systems`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      if (healthSystemsRes.ok) {
        const data = await healthSystemsRes.json();
        setHealthSystems(data.health_systems || []);
      }
      
      // Fetch live on-site contractors
      fetchLiveOnSiteContractors();
      
      toast.success("Data refreshed successfully");
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to refresh data: " + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch live on-site contractors for real-time tracking
  const fetchLiveOnSiteContractors = async () => {
    try {
      setLoadingLiveTracking(true);
      const res = await fetch(`${API}/admin/contractors/on-site`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLiveOnSiteContractors(data.contractors || []);
      }
    } catch (err) {
      console.error("Failed to fetch live tracking:", err);
    } finally {
      setLoadingLiveTracking(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleCreateWorkOrder = async () => {
    if (!selectedItem || !workOrderForm.scope_of_work) return;
    
    try {
      const response = await fetch(`${API}/maintenance/work-orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          request_id: selectedItem.id,
          scope_of_work: workOrderForm.scope_of_work,
          estimated_hours: workOrderForm.estimated_hours ? parseFloat(workOrderForm.estimated_hours) : null,
          estimated_cost: workOrderForm.estimated_cost ? parseFloat(workOrderForm.estimated_cost) : null,
          special_instructions: workOrderForm.special_instructions,
          priority: workOrderForm.priority,
          terms: workOrderForm.terms,
          due_date: workOrderForm.due_date,
          assigned_contractors: workOrderForm.assigned_contractors,
          assigned_suppliers: workOrderForm.assigned_suppliers,
          additional_contacts: workOrderForm.additional_contacts,
          send_notifications: workOrderForm.send_notifications
        })
      });

      if (response.ok) {
        const data = await response.json();
        const notifCount = data.notifications_sent ? 
          (data.notifications_sent.contractors + data.notifications_sent.suppliers + data.notifications_sent.contacts) : 0;
        toast.success(`Work Order ${data.work_order_id} created${notifCount > 0 ? ` - ${notifCount} notification(s) sent` : ''}`);
        setShowWorkOrderModal(false);
        setSelectedItem(null);
        setWorkOrderForm({ 
          scope_of_work: "", estimated_hours: "", estimated_cost: "", special_instructions: "", 
          priority: "normal", terms: "Net 30", due_date: "",
          assigned_contractors: [], assigned_suppliers: [], additional_contacts: [], send_notifications: true
        });
        setWoNewContact({ name: "", email: "", phone: "" });
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to create work order");
      }
    } catch (error) {
      toast.error("Failed to create work order");
    }
  };

  const handleDispatch = async () => {
    if (!selectedItem?.work_order_id || !dispatchForm.contractor_id) return;
    
    try {
      const response = await fetch(`${API}/maintenance/work-orders/${selectedItem.work_order_id}/dispatch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          work_order_id: selectedItem.work_order_id,
          ...dispatchForm
        })
      });

      if (response.ok) {
        toast.success("Work order dispatched successfully");
        setShowDispatchModal(false);
        setSelectedItem(null);
        setDispatchForm({ contractor_id: "", dispatch_notes: "", expected_arrival: "" });
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to dispatch");
      }
    } catch (error) {
      toast.error("Failed to dispatch work order");
    }
  };

  const handleCloseWorkOrder = async (workOrderId) => {
    try {
      const response = await fetch(`${API}/maintenance/work-orders/${workOrderId}/close`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Work order closed");
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to close work order");
      }
    } catch (error) {
      toast.error("Failed to close work order");
    }
  };

  const handleSelectHealthSystem = (systemId) => {
    const system = healthSystems.find(h => h.id === systemId);
    if (system) {
      setInternalRequestForm({
        ...internalRequestForm,
        hospital_id: systemId,
        hospital_name: system.name,
        facility_id: "",
        facility_name: "",
        contact_email: system.primary_contact_email || "",
        contact_name: system.primary_contact_name || "",
        contact_phone: system.primary_contact_phone || "",
        notify_users: []
      });
      setFacilities(system.facilities || []);
      // Fetch assets for this health system
      fetchAssets(systemId);
      // Fetch hospital users for notification selection
      fetchHospitalUsersForNotification(systemId);
    }
  };

  const handleSelectFacility = (facilityId) => {
    if (facilityId === "add_new") {
      setShowAddFacilityModal(true);
      return;
    }
    const facility = facilities.find(f => f.id === facilityId);
    if (facility) {
      setInternalRequestForm({
        ...internalRequestForm,
        facility_id: facilityId,
        facility_name: facility.name
      });
    }
  };

  const handleAddNewFacility = async () => {
    if (!newFacilityForm.name) {
      toast.error("Facility name is required");
      return;
    }
    if (!internalRequestForm.hospital_id) {
      toast.error("Please select a health system first");
      return;
    }

    try {
      const response = await fetch(`${API}/admin/health-systems/${internalRequestForm.hospital_id}/facilities`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newFacilityForm)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Facility "${newFacilityForm.name}" added successfully`);
        
        // Add the new facility to the local facilities list
        const newFacility = data.facility || { 
          id: data.facility_id, 
          name: newFacilityForm.name,
          ...newFacilityForm
        };
        setFacilities([...facilities, newFacility]);
        
        // Select the new facility
        setInternalRequestForm({
          ...internalRequestForm,
          facility_id: newFacility.id,
          facility_name: newFacility.name
        });
        
        // Reset and close
        setNewFacilityForm({
          name: "",
          facility_type: "hospital",
          address: "",
          city: "",
          state: "",
          zip_code: "",
          phone: ""
        });
        setShowAddFacilityModal(false);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to add facility");
      }
    } catch (error) {
      toast.error("Failed to add facility");
    }
  };

  const fetchAssets = async (hospitalId) => {
    try {
      const response = await fetch(`${API}/assets?hospital_id=${hospitalId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAssets(data.assets || []);
      }
    } catch (error) {
      console.error("Failed to fetch assets:", error);
    }
  };

  // Edit maintenance request
  const openEditModal = (item) => {
    setEditingItem(item);
    setEditForm({
      request_type: item.request_type || item.type || "",
      priority: item.priority || "normal",
      description: item.description || "",
      stage: item.stage || "intake",
      contact_name: item.contact_name || "",
      contact_email: item.contact_email || "",
      contact_phone: item.contact_phone || "",
      purchase_order_number: item.purchase_order_number || "",
      invoice_number: item.invoice_number || "",
      additional_contacts: item.additional_contacts || []
    });
    setShowEditModal(true);
  };

  const handleUpdateRequest = async () => {
    if (!editingItem) return;

    try {
      const response = await fetch(`${API}/admin/maintenance-requests/${editingItem.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        toast.success("Maintenance request updated successfully");
        setShowEditModal(false);
        setEditingItem(null);
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to update request");
      }
    } catch (error) {
      toast.error("Failed to update request");
    }
  };

  // Quick Stage Change - move to next stage without opening edit modal
  const handleQuickStageChange = async (item, newStage) => {
    // Optimistic UI update - move item immediately for smooth UX
    setPipeline(prev => {
      const oldStage = item.stage || "intake";
      const newPipeline = { ...prev };
      
      // Remove from old stage
      newPipeline[oldStage] = (newPipeline[oldStage] || []).filter(i => i.id !== item.id);
      
      // Add to new stage with updated stage value
      const updatedItem = { ...item, stage: newStage };
      newPipeline[newStage] = [updatedItem, ...(newPipeline[newStage] || [])];
      
      return newPipeline;
    });
    
    try {
      const response = await fetch(`${API}/admin/maintenance-requests/${item.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ stage: newStage })
      });

      if (response.ok) {
        const stageLabel = STAGES.find(s => s.key === newStage)?.label || newStage;
        toast.success(`Moved to ${stageLabel}`);
        // Fetch fresh data to ensure sync
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to change stage");
        // Revert on error
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to change stage");
      // Revert on error
      fetchData();
    }
  };

  // Get next stage for quick advance
  const getNextStage = (currentStage) => {
    const stageOrder = ["intake", "triage", "dispatch", "in_progress", "completion", "closed"];
    const currentIndex = stageOrder.indexOf(currentStage);
    if (currentIndex >= 0 && currentIndex < stageOrder.length - 1) {
      return stageOrder[currentIndex + 1];
    }
    return null;
  };

  // Drag and Drop handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (!over) return;
    
    const draggedItemId = active.id;
    let targetStage = over.id;
    
    // Find the item being dragged and its current stage
    let draggedItem = null;
    let currentStage = null;
    
    for (const [stage, items] of Object.entries(pipeline)) {
      const found = items.find(item => item.id === draggedItemId);
      if (found) {
        draggedItem = found;
        currentStage = stage;
        break;
      }
    }
    
    if (!draggedItem) return;
    
    // Check if targetStage is actually a valid stage key
    const validStages = STAGES.map(s => s.key);
    
    // If over.id is not a stage, it might be another item - find what stage that item is in
    if (!validStages.includes(targetStage)) {
      // over.id is an item ID, find which stage it belongs to
      for (const [stage, items] of Object.entries(pipeline)) {
        const found = items.find(item => item.id === targetStage);
        if (found) {
          targetStage = stage;
          break;
        }
      }
    }
    
    // If still not a valid stage or same stage, do nothing
    if (!validStages.includes(targetStage) || currentStage === targetStage) return;
    
    console.log(`Moving ticket from ${currentStage} to ${targetStage}`);
    
    // Update stage via API
    await handleQuickStageChange(draggedItem, targetStage);
  };

  const findItemStage = (itemId) => {
    for (const [stage, items] of Object.entries(pipeline)) {
      if (items.find(item => item.id === itemId)) {
        return stage;
      }
    }
    return null;
  };

  // Delete maintenance request
  const openDeleteConfirm = (item) => {
    setDeletingItem(item);
    setShowDeleteConfirm(true);
  };

  // Bulk delete - open confirmation for multiple items
  const openBulkDeleteConfirm = () => {
    if (selectedForDelete.size === 0) {
      toast.error("No items selected for deletion");
      return;
    }
    setDeletingItems(Array.from(selectedForDelete));
    setShowDeleteConfirm(true);
  };

  // Toggle item selection for bulk delete
  const toggleSelectForDelete = (itemId) => {
    setSelectedForDelete(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleDeleteRequest = async () => {
    // Handle bulk delete
    if (deletingItems.length > 0) {
      let successCount = 0;
      let failCount = 0;
      
      for (const itemId of deletingItems) {
        try {
          const response = await fetch(`${API}/admin/maintenance/${itemId}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch {
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} request(s) deleted successfully`);
      }
      if (failCount > 0) {
        toast.error(`Failed to delete ${failCount} request(s)`);
      }
      
      setShowDeleteConfirm(false);
      setDeletingItems([]);
      setSelectedForDelete(new Set());
      setBulkDeleteMode(false);
      fetchData();
      return;
    }
    
    // Handle single delete
    if (!deletingItem) return;

    try {
      const response = await fetch(`${API}/admin/maintenance/${deletingItem.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Deleted successfully - all collaborators notified");
        setShowDeleteConfirm(false);
        setDeletingItem(null);
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to delete request");
      }
    } catch (error) {
      toast.error("Failed to delete request");
    }
  };

  // Notes management
  const openNotesModal = (item) => {
    setSelectedItem(item);
    setNewNote("");
    setShowNotesModal(true);
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedItem) return;

    try {
      const response = await fetch(`${API}/admin/maintenance-requests/${selectedItem.id}/notes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ note: newNote })
      });

      if (response.ok) {
        toast.success("Note added successfully");
        setNewNote("");
        // Refresh the selected item to show the new note
        const itemRes = await fetch(`${API}/admin/maintenance-requests/${selectedItem.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (itemRes.ok) {
          const data = await itemRes.json();
          setSelectedItem(data.request);
        }
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to add note");
      }
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  // Add Progress Update (Admin can add on behalf of contractor)
  const openAddUpdateModal = (item) => {
    setUpdateItem(item);
    setUpdateMessage("");
    setUpdateStatus("");
    setShowAddUpdateModal(true);
  };

  const handleSubmitUpdate = async () => {
    if (!updateMessage.trim() || !updateItem) return;

    setSubmittingUpdate(true);
    try {
      const response = await fetch(`${API}/maintenance-requests/${updateItem.id}/updates`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          message: updateMessage,
          status_update: updateStatus || null
        })
      });

      if (response.ok) {
        toast.success("Progress update added successfully");
        setShowAddUpdateModal(false);
        setUpdateMessage("");
        setUpdateStatus("");
        setUpdateItem(null);
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to add update");
      }
    } catch (error) {
      toast.error("Failed to add update");
    } finally {
      setSubmittingUpdate(false);
    }
  };

  // Audit Log
  const openAuditLogModal = (item) => {
    setSelectedItem(item);
    setShowAuditLogModal(true);
  };

  // Add Member by Email - Viral Growth Loop
  const handleAddMemberByEmail = async () => {
    if (!addMemberEmail.trim() || !manageAssignmentsItem) return;
    
    setAddingMember(true);
    try {
      const response = await fetch(`${API}/admin/maintenance/${manageAssignmentsItem.id}/tag-by-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email: addMemberEmail.trim() })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.type === "registered_user") {
          toast.success(`✅ ${addMemberEmail} has been tagged and notified via Email & Telegram`);
          // Update local state
          setManageAssignmentsItem(prev => ({
            ...prev,
            tagged_users: data.tagged_users || [...(prev.tagged_users || []), data.user_id]
          }));
        } else if (data.type === "invite_sent") {
          toast.success(`📧 Enterprise Onboarding invitation sent to ${addMemberEmail}`);
          // Update local state with pending invite
          setManageAssignmentsItem(prev => ({
            ...prev,
            pending_invites: [...(prev.pending_invites || []), addMemberEmail]
          }));
        } else if (data.type === "invite_resent") {
          toast.info(`📧 Invitation re-sent to ${addMemberEmail}`);
        } else if (data.status === "already_tagged") {
          toast.info(data.message || "User is already tagged");
        }
        setAddMemberEmail("");
        fetchData();
      } else {
        toast.error(data.detail || "Failed to add member");
      }
    } catch (error) {
      console.error("Error adding member:", error);
      toast.error("Failed to add member");
    } finally {
      setAddingMember(false);
    }
  };

  // Photo upload handler for internal requests
  const handleInternalPhotoUpload = async (file) => {
    if (!file) return;
    
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/heic"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a valid image (JPEG, PNG, WebP, or HEIC)");
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large. Maximum size is 10MB.");
      return;
    }
    
    setUploadingInternalPhoto(true);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await fetch(`${API}/maintenance/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.url) {
        setInternalRequestForm(prev => ({
          ...prev,
          photos: [...(prev.photos || []), data.url]
        }));
        toast.success("Photo uploaded successfully");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload photo");
    } finally {
      setUploadingInternalPhoto(false);
    }
  };

  const removeInternalPhoto = (index) => {
    setInternalRequestForm(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleCreateInternalRequest = async () => {
    if (!internalRequestForm.hospital_id || !internalRequestForm.description) {
      toast.error("Please select a hospital and provide a description");
      return;
    }

    try {
      const response = await fetch(`${API}/admin/internal-maintenance-request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(internalRequestForm)
      });

      if (response.ok) {
        const data = await response.json();
        
        // If contractor is assigned, make the assignment
        if (internalRequestForm.assigned_contractor_id) {
          await fetch(`${API}/admin/maintenance-requests/${data.request_id}/assign-contractor`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              contractor_id: internalRequestForm.assigned_contractor_id,
              contractor_name: internalRequestForm.assigned_contractor_name,
              notes: `Assigned at creation: ${internalRequestForm.notes || ''}`
            })
          });
          toast.success(`Request ${data.request_id} created and assigned to ${internalRequestForm.assigned_contractor_name}`);
        } else {
          toast.success(`Internal maintenance request ${data.request_id} created`);
        }
        
        // Show notification about hospital users being notified
        if (data.hospital_users_notified > 0) {
          toast.info(`${data.hospital_users_notified} hospital user(s) have been notified about this ticket`);
        }
        
        setShowCreateInternalModal(false);
        setInternalRequestForm({
          hospital_id: "",
          hospital_name: "",
          facility_id: "",
          facility_name: "",
          request_type: "preventive_maintenance",
          priority: "normal",
          description: "",
          asset_id: "",
          asset_name: "",
          contact_name: "",
          contact_email: "",
          contact_phone: "",
          scheduled_date: "",
          notes: "",
          assigned_contractor_id: "",
          assigned_contractor_name: "",
          notify_users: [],
          photos: [],
          additional_contacts: []
        });
        setNewContact({ name: "", email: "", phone: "", role: "hospital_contact" });
        setHospitalUsersForNotify([]);
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to create internal request");
      }
    } catch (error) {
      toast.error("Failed to create internal request");
    }
  };

  // Sync contractor assignment with full notification loop
  const handleSyncContractor = async () => {
    if (!selectedItem || !syncContractorEmail) {
      toast.error("Please select a contractor email");
      return;
    }
    
    setSyncingContractor(true);
    setSyncStatus(null);
    
    try {
      const response = await fetch(`${API}/admin/maintenance/sync-contractor-assignment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ticket_id: selectedItem.id,
          contractor_email: syncContractorEmail,
          force_reassign: true,
          send_notifications: true,
          priority: syncPriority
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSyncStatus({
          success: true,
          ...data
        });
        toast.success(`✅ Synced to ${data.contractor?.company_name || syncContractorEmail}`);
        
        // Refresh data
        fetchData();
      } else {
        setSyncStatus({
          success: false,
          error: data.detail || "Sync failed"
        });
        toast.error(data.detail || "Failed to sync contractor");
      }
    } catch (error) {
      setSyncStatus({
        success: false,
        error: error.message
      });
      toast.error("Failed to sync contractor assignment");
    } finally {
      setSyncingContractor(false);
    }
  };
  
  // Check sync status for a ticket
  const checkSyncStatus = async (ticketId) => {
    try {
      const response = await fetch(`${API}/admin/maintenance/${ticketId}/sync-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data);
      }
    } catch (error) {
      console.error("Failed to check sync status:", error);
    }
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      emergency: "bg-red-100 text-red-700 border-red-200",
      high: "bg-orange-100 text-orange-700 border-orange-200",
      normal: "bg-blue-100 text-blue-700 border-blue-200",
      low: "bg-slate-100 text-slate-600 border-slate-200"
    };
    return styles[priority] || styles.normal;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 font-manrope">Maintenance Pipeline</h2>
          <p className="text-slate-500 text-sm">Track and manage maintenance requests through each stage</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button 
              onClick={() => setShowCreateInternalModal(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="create-internal-request-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Internal Request
            </Button>
          )}
          <Button 
            variant={viewMode === "pipeline" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("pipeline")}
            className={viewMode === "pipeline" ? "bg-blue-600 text-white" : ""}
          >
            <Layers className="w-4 h-4 mr-1" />
            Pipeline
          </Button>
          <Button 
            variant={viewMode === "table" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("table")}
            className={viewMode === "table" ? "bg-blue-600 text-white" : ""}
          >
            <ClipboardList className="w-4 h-4 mr-1" />
            Table
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {isSuperAdmin && (
            <>
              <Button 
                variant={bulkDeleteMode ? "destructive" : "outline"} 
                size="sm" 
                onClick={() => {
                  setBulkDeleteMode(!bulkDeleteMode);
                  if (bulkDeleteMode) {
                    setSelectedForDelete(new Set());
                  }
                }}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {bulkDeleteMode ? "Cancel Bulk" : "Bulk Delete"}
              </Button>
              {bulkDeleteMode && selectedForDelete.size > 0 && (
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={openBulkDeleteConfirm}
                >
                  Delete ({selectedForDelete.size})
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Stage Summary Cards */}
      <div className="grid grid-cols-6 gap-3">
        {STAGES.map((stage) => (
          <div key={stage.key} className="bg-white border border-slate-200 rounded-lg p-3 text-center">
            <div className={`w-8 h-8 ${stage.color} rounded-lg flex items-center justify-center mx-auto mb-2`}>
              <stage.icon className="w-4 h-4 text-white" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{counts[stage.key] || 0}</p>
            <p className="text-xs text-slate-500 uppercase tracking-wide">{stage.label}</p>
          </div>
        ))}
      </div>

      {viewMode === "pipeline" ? (
        /* Kanban Pipeline View with Drag and Drop */
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {/* Pipeline Controls Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-slate-600">
              {isSuperAdmin && (
                <div className="px-3 py-2 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg text-blue-700 flex items-center gap-2">
                  <GripVertical className="w-4 h-4" />
                  <span><strong>Super Admin:</strong> Use &quot;Move...&quot; dropdown on each card or drag via the blue handle</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Pipeline Columns with Floating Scroll Arrows */}
          <div className="relative">
            {/* Left Floating Scroll Arrow - Mid Screen */}
            <button
              onClick={() => {
                const container = document.querySelector('.pipeline-scroll-container');
                if (container) container.scrollLeft -= 300;
              }}
              className="fixed left-4 top-1/2 -translate-y-1/2 z-[100] bg-blue-600 hover:bg-blue-700 text-white w-12 h-12 rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 border-2 border-white"
              title="Scroll Left"
              style={{ top: 'calc(50vh)' }}
            >
              <ChevronLeft className="w-7 h-7" />
            </button>
            
            {/* Right Floating Scroll Arrow - Mid Screen */}
            <button
              onClick={() => {
                const container = document.querySelector('.pipeline-scroll-container');
                if (container) container.scrollLeft += 300;
              }}
              className="fixed right-4 top-1/2 -translate-y-1/2 z-[100] bg-blue-600 hover:bg-blue-700 text-white w-12 h-12 rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 border-2 border-white"
              title="Scroll Right"
              style={{ top: 'calc(50vh)' }}
            >
              <ChevronRight className="w-7 h-7" />
            </button>
            
            <div className="pipeline-scroll-container w-full overflow-x-auto pb-4 scroll-smooth" style={{ WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth' }}>
              <div className="flex gap-3 min-w-max px-1">
                {STAGES.map((stage) => (
                  <div key={stage.key} className="w-[280px] flex-shrink-0">
                    <div className={`${stage.color} text-white px-3 py-2 rounded-t-lg flex items-center justify-between`}>
                    <div className="flex items-center gap-2">
                      <stage.icon className="w-4 h-4" />
                    <span className="font-medium text-sm">{stage.label}</span>
                  </div>
                  <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">{counts[stage.key] || 0}</span>
                </div>
                
                <SortableContext 
                  items={(pipeline[stage.key] || []).map(item => item.id)}
                  strategy={verticalListSortingStrategy}
                  id={stage.key}
                >
                  <DroppableStage stageKey={stage.key}>
                    {(pipeline[stage.key] || []).map((item) => (
                      <DraggableCard key={item.id} item={item} isSuperAdmin={isSuperAdmin}>
                        <div 
                          className={`bg-white border rounded-lg p-2 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
                            selectedForDelete.has(item.id) ? 'border-red-400 ring-2 ring-red-200 bg-red-50' : 'border-slate-200'
                          }`}
                          onClick={() => {
                            if (bulkDeleteMode) {
                              toggleSelectForDelete(item.id);
                            } else {
                              setSelectedItem(item); 
                              setShowDetailModal(true);
                            }
                          }}
                          data-testid={`pipeline-card-${item.id}`}
                        >
                          {/* Bulk Delete Checkbox */}
                          {bulkDeleteMode && (
                            <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-200">
                              <input
                                type="checkbox"
                                checked={selectedForDelete.has(item.id)}
                                onChange={() => toggleSelectForDelete(item.id)}
                                onClick={(e) => e.stopPropagation()}
                                className="w-4 h-4 rounded border-red-300 text-red-600 focus:ring-red-500"
                              />
                              <span className="text-xs text-red-600">Select for delete</span>
                            </div>
                          )}
                          <div className="flex items-start justify-between mb-1 gap-1">
                            <span className="text-xs font-mono text-slate-500 truncate flex-1">#{item.id?.slice(-8)}</span>
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${getPriorityBadge(item.priority)}`}>
                              {item.priority?.toUpperCase()?.slice(0, 4)}
                            </span>
                          </div>
                          <p className="font-medium text-slate-900 text-xs mb-1 truncate" title={item.type?.replace(/_/g, ' ')}>
                            {item.type?.replace(/_/g, ' ')?.slice(0, 20)}{item.type?.length > 20 ? '...' : ''}
                          </p>
                          <p className="text-xs text-slate-600 mb-1 truncate" title={item.facility || item.hospital_name}>
                            {(item.facility || item.hospital_name)?.slice(0, 18)}{(item.facility || item.hospital_name)?.length > 18 ? '...' : ''}
                          </p>
                          <div className="flex items-center justify-between text-xs text-slate-400">
                            <span className="truncate flex-1">{item.contact?.split(' ')[0]}</span>
                            <span className="whitespace-nowrap ml-1">{item.created_at ? new Date(item.created_at).toLocaleDateString('en-US', {month: 'numeric', day: 'numeric'}) : ''}</span>
                          </div>
                          {item.work_order_id && (
                            <div className="mt-1 pt-1 border-t border-slate-100">
                              <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded truncate block">{item.work_order_id}</span>
                            </div>
                          )}
                          
                          {/* Stage-specific actions */}
                          <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                            {/* Row 1: Primary action + Move dropdown */}
                            <div className="flex gap-1">
                              {stage.key === "intake" && (
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="flex-1 h-6 text-xs px-1"
                                  onClick={(e) => { e.stopPropagation(); setSelectedItem(item); setShowWorkOrderModal(true); }}
                                >
                                  <FileText className="w-3 h-3 mr-0.5" />
                                  WO
                                </Button>
                              )}
                              {stage.key === "triage" && item.work_order_id && (
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="flex-1 h-6 text-xs px-1"
                                  onClick={(e) => { e.stopPropagation(); setSelectedItem(item); setShowDispatchModal(true); }}
                                >
                                  <Send className="w-3 h-3 mr-0.5" />
                                  Dispatch
                                </Button>
                              )}
                              {stage.key === "completion" && (
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="flex-1 h-6 text-xs px-1 bg-emerald-50 text-emerald-700"
                                  onClick={(e) => { e.stopPropagation(); handleCloseWorkOrder(item.work_order_id); }}
                                >
                                  <Archive className="w-3 h-3 mr-0.5" />
                                  Close
                                </Button>
                              )}
                              
                              {/* Quick Stage Change */}
                              {isAdmin && stage.key !== "closed" && (
                                <Select
                                  value=""
                                  onValueChange={(newStage) => {
                                    if (newStage && newStage !== stage.key) {
                                      handleQuickStageChange(item, newStage);
                                    }
                                  }}
                                >
                                  <SelectTrigger 
                                    className="flex-1 h-6 text-xs px-1 bg-slate-50 min-w-0"
                                    onClick={(e) => e.stopPropagation()}
                                    data-testid={`quick-stage-${item.id}`}
                                  >
                                    <SelectValue placeholder="Move..." />
                                  </SelectTrigger>
                                  <SelectContent onClick={(e) => e.stopPropagation()}>
                                    {STAGES.filter(s => s.key !== stage.key).map(s => (
                                      <SelectItem key={s.key} value={s.key}>
                                        <div className="flex items-center gap-1">
                                          <div className={`w-2 h-2 rounded-full ${s.color}`} />
                                          {s.label}
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                            
                            {/* Row 2: Secondary actions */}
                            <div className="flex gap-1">
                              {/* Admin PO & Work Scope Button */}
                              {isAdmin && (
                              <>
                                {/* Manage Team (Contractors/Suppliers) */}
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="flex-1 h-6 text-xs px-1 text-orange-600 border-orange-200"
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    setManageAssignmentsItem(item);
                                    setShowManageAssignmentsModal(true);
                                  }}
                                  data-testid={`manage-team-${item.id}`}
                                  title="Manage Contractors & Suppliers"
                                >
                                  <Users className="w-3 h-3" />
                                </Button>
                                {/* Add Progress Update Button */}
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="flex-1 h-6 text-xs px-1 text-emerald-600 border-emerald-200"
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    openAddUpdateModal(item);
                                  }}
                                  data-testid={`add-update-${item.id}`}
                                  title="Add Progress Update"
                                >
                                  <MessageSquare className="w-3 h-3" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="flex-1 h-6 text-xs px-1 text-purple-600"
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    setEntityDetailId(item.id); 
                                    setShowEntityDetailModal(true); 
                                  }}
                                  data-testid={`maintenance-po-scope-${item.id}`}
                                  title="PO & Scope"
                                >
                                  <Settings className="w-3 h-3" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="flex-1 h-6 text-xs px-1 text-blue-600"
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    setSendEmailEntityId(item.id);
                                    setSendEmailEntityName(item.issue_type?.replace(/_/g, ' ') || 'Maintenance');
                                    setShowSendEmailModal(true); 
                                  }}
                                  data-testid={`maintenance-email-${item.id}`}
                                  title="Send Email (Azure)"
                                >
                                  <Mail className="w-3 h-3" />
                                </Button>
                                <a 
                                  href={`mailto:${item.contact_email || ''}?subject=${encodeURIComponent(`Update for Ticket #${item.id} - sterilemate.com`)}&body=${encodeURIComponent(`Ticket Update\n\nTicket ID: ${item.id}\nCurrent Status: ${item.stage || item.status || 'Unknown'}\nFacility: ${item.facility || item.hospital_name || ''}\nDescription: ${item.description?.slice(0, 200) || ''}\n\nView ticket details: https://sterilemate.com/admin/maintenance/${item.id}\n\n---\nAsk Isaac Team`)}`}
                                  onClick={(e) => e.stopPropagation()}
                                  title="Send Manual Update (Outlook)"
                                >
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="flex-1 h-6 text-xs px-1 text-orange-600"
                                    asChild
                                  >
                                    <span><ExternalLink className="w-3 h-3" /></span>
                                  </Button>
                                </a>
                                {/* Delete Button for Super Admin */}
                                {isSuperAdmin && (
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="flex-1 h-6 text-xs px-1 text-red-600 border-red-200 hover:bg-red-50"
                                    onClick={(e) => { 
                                      e.stopPropagation(); 
                                      openDeleteConfirm(item);
                                    }}
                                    data-testid={`delete-ticket-${item.id}`}
                                    title="Delete Ticket"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </>
                            )}
                            </div>
                          </div>
                        </div>
                      </DraggableCard>
                    ))}
                    
                    {(pipeline[stage.key] || []).length === 0 && (
                      <div className="text-center py-8 text-slate-400 text-sm">
                        No items
                      </div>
                    )}
                  </DroppableStage>
                </SortableContext>
              </div>
            ))}
            </div>
          </div>
          </div>
          {/* Scroll hint for pipeline */}
          <div className="text-center text-xs text-slate-400 mt-2">
            <span>Use the floating arrows on the sides to scroll between stages</span>
          </div>
        </DndContext>
      ) : (
        /* Table View */
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Facility</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Priority</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Stage</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Work Order</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {Object.entries(pipeline).flatMap(([stage, items]) =>
                items.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-mono text-slate-900">#{item.id}</td>
                    <td className="px-4 py-3 text-sm text-slate-900">{item.type}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{item.facility}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityBadge(item.priority)}`}>
                        {item.priority?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        STAGES.find(s => s.key === stage)?.color
                      } text-white`}>
                        {STAGES.find(s => s.key === stage)?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-blue-600">{item.work_order_id || "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{new Date(item.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => { setSelectedItem(item); setShowDetailModal(true); }}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => openEditModal(item)}
                              title="Edit"
                              className="text-blue-600 hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => openNotesModal(item)}
                              title="Notes"
                              className="text-amber-600 hover:bg-amber-50"
                            >
                              <MessageSquare className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => openAuditLogModal(item)}
                              title="Activity Log"
                              className="text-purple-600 hover:bg-purple-50"
                            >
                              <History className="w-4 h-4" />
                            </Button>
                            {isSuperAdmin && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => {
                                  console.log("Delete clicked for item:", item.id);
                                  openDeleteConfirm(item);
                                }}
                                title="Delete"
                                className="text-red-600 hover:bg-red-50"
                                data-testid={`table-delete-${item.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Work Order Modal */}
      <Dialog open={showWorkOrderModal} onOpenChange={setShowWorkOrderModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Create Work Order
            </DialogTitle>
            <DialogDescription>
              Create a work order for request #{selectedItem?.id}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Scope of Work *</Label>
              <Textarea
                value={workOrderForm.scope_of_work}
                onChange={(e) => setWorkOrderForm({...workOrderForm, scope_of_work: e.target.value})}
                placeholder="Describe the work to be performed..."
                className="mt-1"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Estimated Hours</Label>
                <Input
                  type="number"
                  value={workOrderForm.estimated_hours}
                  onChange={(e) => setWorkOrderForm({...workOrderForm, estimated_hours: e.target.value})}
                  placeholder="4"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Estimated Cost ($)</Label>
                <Input
                  type="number"
                  value={workOrderForm.estimated_cost}
                  onChange={(e) => setWorkOrderForm({...workOrderForm, estimated_cost: e.target.value})}
                  placeholder="500"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Priority</Label>
                <Select value={workOrderForm.priority} onValueChange={(v) => setWorkOrderForm({...workOrderForm, priority: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Terms</Label>
                <Select value={workOrderForm.terms} onValueChange={(v) => setWorkOrderForm({...workOrderForm, terms: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Net 15">Net 15</SelectItem>
                    <SelectItem value="Net 30">Net 30</SelectItem>
                    <SelectItem value="Net 45">Net 45</SelectItem>
                    <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Special Instructions</Label>
              <Textarea
                value={workOrderForm.special_instructions}
                onChange={(e) => setWorkOrderForm({...workOrderForm, special_instructions: e.target.value})}
                placeholder="Any special instructions for the contractor..."
                className="mt-1"
                rows={2}
              />
            </div>

            {/* Assignees Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Assign to Work Order
              </h4>
              
              {/* Contractors Selection */}
              <div className="mb-3">
                <Label className="text-sm">Contractors</Label>
                <Select 
                  value="" 
                  onValueChange={(v) => {
                    if (v && !workOrderForm.assigned_contractors.includes(v)) {
                      setWorkOrderForm({...workOrderForm, assigned_contractors: [...workOrderForm.assigned_contractors, v]});
                    }
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select contractor to add..." />
                  </SelectTrigger>
                  <SelectContent>
                    {contractors.filter(c => !workOrderForm.assigned_contractors.includes(c.id)).map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.company_name || c.name} - {c.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {workOrderForm.assigned_contractors.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {workOrderForm.assigned_contractors.map(cId => {
                      const c = contractors.find(x => x.id === cId);
                      return (
                        <span key={cId} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          {c?.company_name || c?.name || cId}
                          <button type="button" onClick={() => setWorkOrderForm({...workOrderForm, assigned_contractors: workOrderForm.assigned_contractors.filter(x => x !== cId)})} className="hover:text-blue-600">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Suppliers Selection */}
              <div className="mb-3">
                <Label className="text-sm">Suppliers</Label>
                <Select 
                  value="" 
                  onValueChange={(v) => {
                    if (v && !workOrderForm.assigned_suppliers.includes(v)) {
                      setWorkOrderForm({...workOrderForm, assigned_suppliers: [...workOrderForm.assigned_suppliers, v]});
                    }
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select supplier to add..." />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.filter(s => !workOrderForm.assigned_suppliers.includes(s.id)).map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.company_name || s.name} - {s.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {workOrderForm.assigned_suppliers.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {workOrderForm.assigned_suppliers.map(sId => {
                      const s = suppliers.find(x => x.id === sId);
                      return (
                        <span key={sId} className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                          {s?.company_name || s?.name || sId}
                          <button type="button" onClick={() => setWorkOrderForm({...workOrderForm, assigned_suppliers: workOrderForm.assigned_suppliers.filter(x => x !== sId)})} className="hover:text-green-600">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Additional Contacts */}
              <div className="mb-3">
                <Label className="text-sm">Additional Contacts</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  <Input
                    placeholder="Name"
                    value={woNewContact.name}
                    onChange={(e) => setWoNewContact({...woNewContact, name: e.target.value})}
                    className="text-sm"
                  />
                  <Input
                    placeholder="Email"
                    type="email"
                    value={woNewContact.email}
                    onChange={(e) => setWoNewContact({...woNewContact, email: e.target.value})}
                    className="text-sm"
                  />
                  <div className="flex gap-1">
                    <Input
                      placeholder="Phone"
                      value={woNewContact.phone}
                      onChange={(e) => setWoNewContact({...woNewContact, phone: e.target.value})}
                      className="text-sm"
                    />
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        if (woNewContact.name && woNewContact.email) {
                          setWorkOrderForm({...workOrderForm, additional_contacts: [...workOrderForm.additional_contacts, {...woNewContact}]});
                          setWoNewContact({ name: "", email: "", phone: "" });
                        }
                      }}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {workOrderForm.additional_contacts.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {workOrderForm.additional_contacts.map((contact, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                        {contact.name} ({contact.email})
                        <button type="button" onClick={() => setWorkOrderForm({...workOrderForm, additional_contacts: workOrderForm.additional_contacts.filter((_, i) => i !== idx)})} className="hover:text-purple-600">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Send Notifications Toggle */}
              <div className="flex items-center gap-2 mt-3">
                <input
                  type="checkbox"
                  id="send_notifications"
                  checked={workOrderForm.send_notifications}
                  onChange={(e) => setWorkOrderForm({...workOrderForm, send_notifications: e.target.checked})}
                  className="rounded border-slate-300"
                />
                <Label htmlFor="send_notifications" className="text-sm cursor-pointer">
                  Send email notifications to all assignees
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWorkOrderModal(false)}>Cancel</Button>
            <Button onClick={handleCreateWorkOrder} className="bg-blue-600 hover:bg-blue-700">
              Create Work Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispatch Modal */}
      <Dialog open={showDispatchModal} onOpenChange={setShowDispatchModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-600" />
              Dispatch Work Order
            </DialogTitle>
            <DialogDescription>
              Assign {selectedItem?.work_order_id} to a contractor
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Select Contractor *</Label>
              <Select value={dispatchForm.contractor_id} onValueChange={(v) => setDispatchForm({...dispatchForm, contractor_id: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a contractor..." />
                </SelectTrigger>
                <SelectContent>
                  {contractors.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.company_name} - {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Expected Arrival</Label>
              <Input
                type="datetime-local"
                value={dispatchForm.expected_arrival}
                onChange={(e) => setDispatchForm({...dispatchForm, expected_arrival: e.target.value})}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Dispatch Notes</Label>
              <Textarea
                value={dispatchForm.dispatch_notes}
                onChange={(e) => setDispatchForm({...dispatchForm, dispatch_notes: e.target.value})}
                placeholder="Notes for the contractor..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDispatchModal(false)}>Cancel</Button>
            <Button onClick={handleDispatch} className="bg-blue-600 hover:bg-blue-700">
              <Send className="w-4 h-4 mr-2" />
              Dispatch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-blue-600" />
              Request #{selectedItem?.id}
            </DialogTitle>
            <DialogDescription>
              {selectedItem?.type} • {selectedItem?.facility}
            </DialogDescription>
          </DialogHeader>

          {selectedItem && (
            <div className="space-y-4">
              {/* Request Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Request Details</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getPriorityBadge(selectedItem.priority)}`}>
                        {selectedItem.priority?.toUpperCase()}
                      </span>
                      <span className="text-sm text-slate-600">{selectedItem.type}</span>
                    </div>
                    {selectedItem.description && (
                      <p className="text-sm text-slate-700 mt-2">{selectedItem.description}</p>
                    )}
                  </div>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Facility</p>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <Building2 className="w-4 h-4 text-slate-400 mt-0.5" />
                      <span className="font-medium">{selectedItem.facility}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Users className="w-4 h-4 text-slate-400 mt-0.5" />
                      <span>{selectedItem.contact}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Calendar className="w-4 h-4 text-slate-400 mt-0.5" />
                      <span>{new Date(selectedItem.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Assigned Team Section - Always visible */}
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200" data-testid="assigned-team-section">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-orange-600 uppercase tracking-wide font-medium flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Assigned Team
                  </p>
                  {isAdmin && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="h-7 text-xs border-orange-300 text-orange-700 hover:bg-orange-100"
                      onClick={() => {
                        setManageAssignmentsItem(selectedItem);
                        setShowManageAssignmentsModal(true);
                      }}
                      data-testid="manage-team-from-detail"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Manage Team
                    </Button>
                  )}
                </div>
                
                {/* Contractors */}
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                    <Wrench className="w-3 h-3" /> Contractors ({(selectedItem.assigned_contractors || []).length})
                  </h4>
                  {(selectedItem.assigned_contractors || []).length > 0 ? (
                    <div className="space-y-2">
                      {(selectedItem.assigned_contractors || []).map((contractorId, idx) => {
                        const contractor = contractors.find(c => c.id === contractorId);
                        return (
                          <div key={idx} className="bg-white p-2 rounded border border-orange-100 flex items-center justify-between" data-testid={`assigned-contractor-${contractorId}`}>
                            <div>
                              <p className="font-medium text-sm text-slate-900">{contractor?.name || contractor?.company_name || 'Unknown Contractor'}</p>
                              <div className="flex items-center gap-3 text-xs text-slate-500">
                                {contractor?.email && (
                                  <a href={`mailto:${contractor.email}`} className="hover:text-orange-600 flex items-center gap-1">
                                    <Mail className="w-3 h-3" /> {contractor.email}
                                  </a>
                                )}
                                {contractor?.phone && (
                                  <a href={`tel:${contractor.phone}`} className="hover:text-orange-600 flex items-center gap-1">
                                    📞 {contractor.phone}
                                  </a>
                                )}
                              </div>
                              {contractor?.categories && contractor.categories.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {contractor.categories.slice(0, 3).map((cat, i) => (
                                    <span key={i} className="text-xs px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">{cat}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">Active</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 italic">No contractors assigned</p>
                  )}
                  
                  {/* Sync to Contractor Button */}
                  {isAdmin && (
                    <Button
                      onClick={() => {
                        // Pre-fill with assigned contractor email if available
                        const assignedContractorId = selectedItem.assigned_contractors?.[0] || selectedItem.assigned_contractor_id;
                        const contractor = contractors.find(c => c.id === assignedContractorId);
                        if (contractor?.email) {
                          setSyncContractorEmail(contractor.email);
                        } else {
                          setSyncContractorEmail("");
                        }
                        setSyncPriority(selectedItem.priority || "normal");
                        setSyncStatus(null);
                        setShowSyncModal(true);
                      }}
                      variant="outline"
                      size="sm"
                      className="w-full mt-3 border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="sync-contractor-btn"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Sync to Contractor
                    </Button>
                  )}
                </div>
                
                {/* Suppliers */}
                <div>
                  <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                    <Building2 className="w-3 h-3" /> Suppliers ({(selectedItem.assigned_suppliers || []).length})
                  </h4>
                  {(selectedItem.assigned_suppliers || []).length > 0 ? (
                    <div className="space-y-2">
                      {(selectedItem.assigned_suppliers || []).map((supplier, idx) => {
                        // Get full supplier details from suppliers list
                        const fullSupplier = suppliers.find(s => s.id === supplier.supplier_id);
                        return (
                          <div key={idx} className="bg-white p-2 rounded border border-orange-100 flex items-center justify-between" data-testid={`assigned-supplier-${supplier.supplier_id}`}>
                            <div>
                              <p className="font-medium text-sm text-slate-900">{supplier.supplier_name || fullSupplier?.name || 'Unknown Supplier'}</p>
                              <div className="flex items-center gap-3 text-xs text-slate-500">
                                {(supplier.supplier_email || fullSupplier?.email) && (
                                  <a href={`mailto:${supplier.supplier_email || fullSupplier?.email}`} className="hover:text-orange-600 flex items-center gap-1">
                                    <Mail className="w-3 h-3" /> {supplier.supplier_email || fullSupplier?.email}
                                  </a>
                                )}
                                {fullSupplier?.phone && (
                                  <a href={`tel:${fullSupplier.phone}`} className="hover:text-orange-600 flex items-center gap-1">
                                    📞 {fullSupplier.phone}
                                  </a>
                                )}
                              </div>
                              {supplier.role && (
                                <span className="text-xs text-slate-400 capitalize">Role: {supplier.role}</span>
                              )}
                            </div>
                            <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full capitalize">{supplier.role || 'Vendor'}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 italic">No suppliers assigned</p>
                  )}
                </div>
              </div>

              {/* Work Order Info */}
              {selectedItem.work_order && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-600 uppercase tracking-wide mb-2 font-medium">Work Order: {selectedItem.work_order_id}</p>
                  <div className="space-y-2 text-sm">
                    <p><strong>Scope:</strong> {selectedItem.work_order.scope_of_work}</p>
                    {selectedItem.work_order.estimated_hours && (
                      <p><strong>Est. Hours:</strong> {selectedItem.work_order.estimated_hours}</p>
                    )}
                    {selectedItem.work_order.estimated_cost && (
                      <p><strong>Est. Cost:</strong> ${selectedItem.work_order.estimated_cost}</p>
                    )}
                    {selectedItem.work_order.dispatched_to_name && (
                      <p><strong>Assigned To:</strong> {selectedItem.work_order.dispatched_to_name}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Completion Certificate */}
              {selectedItem.work_order?.completion_certificate && (
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <p className="text-xs text-emerald-600 uppercase tracking-wide mb-2 font-medium">Completion Certificate</p>
                  <div className="space-y-2 text-sm">
                    <p><strong>Work Completed:</strong> {selectedItem.work_order.completion_certificate.work_completed}</p>
                    <p><strong>Labor Hours:</strong> {selectedItem.work_order.completion_certificate.labor_hours}</p>
                    {selectedItem.work_order.completion_certificate.customer_name && (
                      <p><strong>Signed By:</strong> {selectedItem.work_order.completion_certificate.customer_name}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Activity Log */}
              {selectedItem.work_order?.activity_log && (
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Activity Timeline</p>
                  <div className="space-y-2">
                    {selectedItem.work_order.activity_log.map((log, idx) => (
                      <div key={idx} className="flex items-start gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5" />
                        <div>
                          <p className="font-medium text-slate-900">{log.action.replace(/_/g, ' ')}</p>
                          <p className="text-slate-500 text-xs">{log.by} • {new Date(log.at).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ticket Chat Section */}
              <TicketChat
                ticketId={selectedItem.id}
                ticketType="maintenance"
                token={token}
                currentUser={{
                  id: user?.id,
                  name: user?.name,
                  email: user?.email,
                  role: user?.role
                }}
              />

              {/* Sign-Offs Section */}
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <p className="text-xs text-purple-600 uppercase tracking-wide mb-3 font-medium flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Sign-Off Status
                </p>
                
                {/* PO & Invoice Numbers */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-white p-2 rounded border border-purple-100">
                    <span className="text-xs text-slate-500">PO #:</span>
                    <p className="font-medium text-sm">{selectedItem.purchase_order_number || "Not assigned"}</p>
                  </div>
                  <div className="bg-white p-2 rounded border border-purple-100">
                    <span className="text-xs text-slate-500">Invoice #:</span>
                    <p className="font-medium text-sm">{selectedItem.invoice_number || "Not assigned"}</p>
                  </div>
                </div>
                
                {/* Sign-off statuses by party */}
                <div className="space-y-2">
                  {["hospital", "contractor", "supplier", "admin"].map(party => {
                    const signOff = selectedItem.sign_offs?.[party];
                    return (
                      <div key={party} className="flex items-center justify-between bg-white p-2 rounded border border-purple-100">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium capitalize">{party}</span>
                        </div>
                        {signOff ? (
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              signOff.status === 'complete' ? 'bg-green-100 text-green-700' :
                              signOff.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {signOff.status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                            <span className="text-xs text-slate-500">{signOff.user_name}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">Pending sign-off</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Can Close indicator */}
                {selectedItem.sign_offs?.hospital?.status === 'complete' && selectedItem.sign_offs?.contractor?.status === 'complete' && (
                  <div className="mt-3 p-2 bg-green-100 border border-green-200 rounded text-sm text-green-700 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    All required parties have signed off. This job can be closed.
                  </div>
                )}
              </div>

              <DialogFooter className="flex justify-between items-center">
                <div className="flex gap-2">
                  {isSuperAdmin && (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => {
                        setShowDetailModal(false);
                        openDeleteConfirm(selectedItem);
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  )}
                  {isAdmin && (
                    <Button
                      size="sm"
                      onClick={() => {
                        // Pre-fill with assigned contractor email if available
                        const assignedContractorId = selectedItem?.assigned_contractors?.[0] || selectedItem?.assigned_contractor_id;
                        const contractor = contractors.find(c => c.id === assignedContractorId);
                        if (contractor?.email) {
                          setSyncContractorEmail(contractor.email);
                        } else {
                          setSyncContractorEmail("");
                        }
                        setSyncPriority(selectedItem?.priority || "normal");
                        setSyncStatus(null);
                        setShowDetailModal(false);
                        setShowSyncModal(true);
                      }}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="sync-contractor-btn-footer"
                    >
                      <Zap className="w-4 h-4 mr-1" />
                      Sync to Contractor
                    </Button>
                  )}
                </div>
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Entity Detail Modal (PO & Work Scope) */}
      <EntityDetailModal
        open={showEntityDetailModal}
        onClose={() => {
          setShowEntityDetailModal(false);
          setEntityDetailId(null);
        }}
        entityType="maintenance"
        entityId={entityDetailId}
      />

      {/* Send Update Email Modal */}
      <SendUpdateEmailModal
        open={showSendEmailModal}
        onClose={() => {
          setShowSendEmailModal(false);
          setSendEmailEntityId(null);
          setSendEmailEntityName(null);
        }}
        entityType="maintenance"
        entityId={sendEmailEntityId}
        entityName={sendEmailEntityName}
      />

      {/* Create Internal Maintenance Request Modal */}
      <Dialog open={showCreateInternalModal} onOpenChange={setShowCreateInternalModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-600" />
              Create Internal Maintenance Request
            </DialogTitle>
            <DialogDescription>
              Create a maintenance request on behalf of a hospital for preventive maintenance or scheduled work.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Hospital Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Health System / Hospital *</Label>
                <Select 
                  value={internalRequestForm.hospital_id} 
                  onValueChange={handleSelectHealthSystem}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a health system..." />
                  </SelectTrigger>
                  <SelectContent>
                    {healthSystems.map(h => (
                      <SelectItem key={h.id} value={h.id}>
                        {h.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Facility (Optional)</Label>
                <Select 
                  value={internalRequestForm.facility_id} 
                  onValueChange={handleSelectFacility}
                  disabled={!internalRequestForm.hospital_id}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select facility..." />
                  </SelectTrigger>
                  <SelectContent>
                    {facilities.map(f => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name}
                      </SelectItem>
                    ))}
                    {internalRequestForm.hospital_id && (
                      <SelectItem value="add_new" className="text-emerald-600 font-medium">
                        <span className="flex items-center gap-2">
                          <Plus className="w-4 h-4" />
                          Add New Facility
                        </span>
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Request Type & Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Request Type *</Label>
                <Select 
                  value={internalRequestForm.request_type} 
                  onValueChange={(v) => setInternalRequestForm({...internalRequestForm, request_type: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preventive_maintenance">Preventive Maintenance</SelectItem>
                    <SelectItem value="scheduled_inspection">Scheduled Inspection</SelectItem>
                    <SelectItem value="filter_replacement">Filter Replacement</SelectItem>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="hvac">HVAC</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="equipment_repair">Equipment Repair</SelectItem>
                    <SelectItem value="calibration">Calibration</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Priority</Label>
                <Select 
                  value={internalRequestForm.priority} 
                  onValueChange={(v) => setInternalRequestForm({...internalRequestForm, priority: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Asset Selection */}
            {assets.length > 0 && (
              <div>
                <Label>Related Asset (Optional)</Label>
                <Select 
                  value={internalRequestForm.asset_id} 
                  onValueChange={(v) => {
                    const asset = assets.find(a => a.id === v);
                    setInternalRequestForm({
                      ...internalRequestForm, 
                      asset_id: v,
                      asset_name: asset?.name || ""
                    });
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select an asset..." />
                  </SelectTrigger>
                  <SelectContent>
                    {assets.map(a => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name} {a.serial_number ? `(${a.serial_number})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Description */}
            <div>
              <Label>Description *</Label>
              <Textarea
                value={internalRequestForm.description}
                onChange={(e) => setInternalRequestForm({...internalRequestForm, description: e.target.value})}
                placeholder="Describe the maintenance work needed..."
                className="mt-1"
                rows={3}
              />
            </div>

            {/* Scheduled Date */}
            <div>
              <Label>Scheduled Date (Optional)</Label>
              <Input
                type="date"
                value={internalRequestForm.scheduled_date}
                onChange={(e) => setInternalRequestForm({...internalRequestForm, scheduled_date: e.target.value})}
                className="mt-1"
              />
            </div>

            {/* Contact Info - Multi-Contact Support */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Contact Information
              </p>
              
              {/* Primary Contact */}
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <Label className="text-xs">Primary Contact Name</Label>
                  <Input
                    value={internalRequestForm.contact_name}
                    onChange={(e) => setInternalRequestForm({...internalRequestForm, contact_name: e.target.value})}
                    placeholder="Name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input
                    type="email"
                    value={internalRequestForm.contact_email}
                    onChange={(e) => setInternalRequestForm({...internalRequestForm, contact_email: e.target.value})}
                    placeholder="email@hospital.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={internalRequestForm.contact_phone}
                    onChange={(e) => setInternalRequestForm({...internalRequestForm, contact_phone: e.target.value})}
                    placeholder="(800) 988-3892"
                    className="mt-1"
                  />
                </div>
              </div>
              
              {/* Additional Contacts List */}
              {(internalRequestForm.additional_contacts || []).length > 0 && (
                <div className="mb-3 space-y-2">
                  <p className="text-xs text-slate-600 font-medium">Additional Contacts:</p>
                  {internalRequestForm.additional_contacts.map((contact, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-white p-2 rounded border">
                      <div className="flex-1 text-sm">
                        <span className="font-medium">{contact.name || contact.email}</span>
                        {contact.name && contact.email && <span className="text-slate-500"> - {contact.email}</span>}
                        {contact.phone && <span className="text-slate-400 text-xs ml-2">{contact.phone}</span>}
                      </div>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{contact.role || 'Contact'}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                        onClick={() => {
                          setInternalRequestForm(prev => ({
                            ...prev,
                            additional_contacts: prev.additional_contacts.filter((_, i) => i !== idx)
                          }));
                        }}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add Additional Contact */}
              <div className="border-t pt-3 mt-3">
                <p className="text-xs text-slate-600 mb-2 flex items-center gap-1">
                  <UserPlus className="w-3 h-3" />
                  Add Additional Contact (registered or unregistered)
                </p>
                <div className="grid grid-cols-4 gap-2">
                  <Input
                    placeholder="Name"
                    value={newContact.name}
                    onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                    className="text-sm"
                  />
                  <Input
                    type="email"
                    placeholder="Email *"
                    value={newContact.email}
                    onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                    className="text-sm"
                  />
                  <Input
                    placeholder="Phone"
                    value={newContact.phone}
                    onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                    className="text-sm"
                  />
                  <Button
                    type="button"
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    disabled={!newContact.email.trim()}
                    onClick={() => {
                      if (newContact.email.trim()) {
                        setInternalRequestForm(prev => ({
                          ...prev,
                          additional_contacts: [...(prev.additional_contacts || []), {...newContact}]
                        }));
                        setNewContact({ name: "", email: "", phone: "", role: "hospital_contact" });
                        toast.success("Contact added");
                      }
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Unregistered contacts will receive an invitation to join the platform
                </p>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Internal Notes</Label>
              <Textarea
                value={internalRequestForm.notes}
                onChange={(e) => setInternalRequestForm({...internalRequestForm, notes: e.target.value})}
                placeholder="Any additional notes..."
                className="mt-1"
                rows={2}
              />
            </div>

            {/* Contractor Assignment */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-700 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Assign Contractor (Optional)
              </p>
              <Select 
                value={internalRequestForm.assigned_contractor_id || "none"} 
                onValueChange={(v) => {
                  if (v === "none") {
                    setInternalRequestForm({
                      ...internalRequestForm, 
                      assigned_contractor_id: "",
                      assigned_contractor_name: ""
                    });
                  } else {
                    const contractor = contractors.find(c => c.id === v);
                    setInternalRequestForm({
                      ...internalRequestForm, 
                      assigned_contractor_id: v,
                      assigned_contractor_name: contractor?.name || contractor?.company_name || ""
                    });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select contractor to assign..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No contractor (assign later)</SelectItem>
                  {contractors.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name || c.company_name} {c.company_name && c.name ? `(${c.company_name})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-blue-600 mt-2">
                Assigning a contractor will automatically move this request to the Dispatch stage.
              </p>
            </div>

            {/* Notify Hospital Users */}
            {internalRequestForm.hospital_id && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm font-medium text-amber-700 mb-3 flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  Tag Hospital Users to Notify
                </p>
                {loadingHospitalUsers ? (
                  <p className="text-sm text-amber-600">Loading users...</p>
                ) : hospitalUsersForNotify.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {hospitalUsersForNotify.map(u => (
                      <label key={u.id} className="flex items-center gap-3 p-2 bg-white rounded border cursor-pointer hover:bg-amber-50">
                        <input
                          type="checkbox"
                          checked={internalRequestForm.notify_users.includes(u.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setInternalRequestForm({
                                ...internalRequestForm,
                                notify_users: [...internalRequestForm.notify_users, u.id]
                              });
                            } else {
                              setInternalRequestForm({
                                ...internalRequestForm,
                                notify_users: internalRequestForm.notify_users.filter(id => id !== u.id)
                              });
                            }
                          }}
                          className="rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900">{u.name}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          u.registration_status === 'active' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {u.registration_status === 'active' ? 'Active' : 'Pending'}
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-amber-600 italic">No hospital users found for this system</p>
                )}
                {internalRequestForm.notify_users.length > 0 && (
                  <p className="text-xs text-amber-700 mt-2 font-medium">
                    {internalRequestForm.notify_users.length} user(s) will be notified when this ticket is created
                  </p>
                )}
              </div>
            )}

            {/* Photo Upload Section */}
            <div className="border-t border-slate-200 pt-4 mt-4">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <Camera className="w-4 h-4 text-slate-500" />
                Add Photos
              </Label>
              <p className="text-xs text-slate-500 mt-1 mb-2">
                Upload or capture photos of the issue (optional)
              </p>
              
              {/* Hidden file inputs */}
              <input
                type="file"
                ref={internalPhotoInputRef}
                accept="image/*"
                className="hidden"
                onChange={(e) => handleInternalPhotoUpload(e.target.files?.[0])}
              />
              <input
                type="file"
                ref={internalCameraInputRef}
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleInternalPhotoUpload(e.target.files?.[0])}
              />
              
              <div className="flex gap-2 mb-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => internalCameraInputRef.current?.click()}
                  disabled={uploadingInternalPhoto}
                  className="flex-1"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Take Photo
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => internalPhotoInputRef.current?.click()}
                  disabled={uploadingInternalPhoto}
                  className="flex-1"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </Button>
              </div>
              
              {uploadingInternalPhoto && (
                <div className="flex items-center gap-2 text-sm text-blue-600 mb-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading photo...
                </div>
              )}
              
              {internalRequestForm.photos?.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                  {internalRequestForm.photos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={photo}
                        alt={`Uploaded ${index + 1}`}
                        className="w-full h-16 object-cover rounded-lg border border-slate-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeInternalPhoto(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateInternalModal(false)}>Cancel</Button>
            <Button onClick={handleCreateInternalRequest} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Facility Modal */}
      <Dialog open={showAddFacilityModal} onOpenChange={setShowAddFacilityModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-600" />
              Add New Facility
            </DialogTitle>
            <DialogDescription>
              Add a new facility to {internalRequestForm.hospital_name || "the selected health system"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Facility Name *</Label>
              <Input
                value={newFacilityForm.name}
                onChange={(e) => setNewFacilityForm({...newFacilityForm, name: e.target.value})}
                placeholder="e.g. LBJ Outpatient Center"
                data-testid="new-facility-name-input"
              />
            </div>

            <div>
              <Label>Street Address</Label>
              <Input
                value={newFacilityForm.address}
                onChange={(e) => setNewFacilityForm({...newFacilityForm, address: e.target.value})}
                placeholder="e.g. 5550 Kelley Street"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>City</Label>
                <Input
                  value={newFacilityForm.city}
                  onChange={(e) => setNewFacilityForm({...newFacilityForm, city: e.target.value})}
                  placeholder="Houston"
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={newFacilityForm.state}
                  onChange={(e) => setNewFacilityForm({...newFacilityForm, state: e.target.value})}
                  placeholder="TX"
                  maxLength={2}
                />
              </div>
              <div>
                <Label>ZIP Code</Label>
                <Input
                  value={newFacilityForm.zip_code}
                  onChange={(e) => setNewFacilityForm({...newFacilityForm, zip_code: e.target.value})}
                  placeholder="77026"
                />
              </div>
            </div>

            <div>
              <Label>Phone</Label>
              <Input
                value={newFacilityForm.phone}
                onChange={(e) => setNewFacilityForm({...newFacilityForm, phone: e.target.value})}
                placeholder="(713) 566-5000"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddFacilityModal(false)}>Cancel</Button>
            <Button onClick={handleAddNewFacility} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Facility
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Maintenance Request Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Maintenance Request
            </DialogTitle>
            <DialogDescription>
              Edit request #{editingItem?.id}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Request Type</Label>
                <Select value={editForm.request_type} onValueChange={(v) => setEditForm({...editForm, request_type: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preventive_maintenance">Preventive Maintenance</SelectItem>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="hvac">HVAC</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="equipment_repair">Equipment Repair</SelectItem>
                    <SelectItem value="filter_replacement">Filter Replacement</SelectItem>
                    <SelectItem value="water_leak">Water Leak</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Priority</Label>
                <Select value={editForm.priority} onValueChange={(v) => setEditForm({...editForm, priority: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Stage</Label>
              <Select value={editForm.stage} onValueChange={(v) => setEditForm({...editForm, stage: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map(stage => (
                    <SelectItem key={stage.key} value={stage.key}>{stage.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                placeholder="Description of the maintenance request..."
                className="mt-1"
                rows={3}
              />
            </div>

            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="text-sm font-medium text-slate-700 mb-3">Contact Information</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input
                    value={editForm.contact_name}
                    onChange={(e) => setEditForm({...editForm, contact_name: e.target.value})}
                    placeholder="Name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input
                    type="email"
                    value={editForm.contact_email}
                    onChange={(e) => setEditForm({...editForm, contact_email: e.target.value})}
                    placeholder="email@hospital.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={editForm.contact_phone}
                    onChange={(e) => setEditForm({...editForm, contact_phone: e.target.value})}
                    placeholder="(800) 988-3892"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* PO & Invoice Numbers */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm font-medium text-amber-800 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Financial References
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Purchase Order #</Label>
                  <Input
                    value={editForm.purchase_order_number}
                    onChange={(e) => setEditForm({...editForm, purchase_order_number: e.target.value})}
                    placeholder="PO-12345"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Invoice #</Label>
                  <Input
                    value={editForm.invoice_number}
                    onChange={(e) => setEditForm({...editForm, invoice_number: e.target.value})}
                    placeholder="INV-12345"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Additional Contacts */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-purple-800 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Additional Contacts
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => setEditForm({
                    ...editForm,
                    additional_contacts: [...(editForm.additional_contacts || []), { name: "", email: "", role: "hospital" }]
                  })}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Contact
                </Button>
              </div>
              <p className="text-xs text-purple-600 mb-3">
                All contacts will be notified of updates to this maintenance request.
              </p>
              {editForm.additional_contacts && editForm.additional_contacts.length > 0 ? (
                <div className="space-y-3">
                  {editForm.additional_contacts.map((contact, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-white p-2 rounded border border-purple-100">
                      <Input
                        placeholder="Name"
                        value={contact.name}
                        onChange={(e) => {
                          const updated = [...editForm.additional_contacts];
                          updated[idx] = { ...updated[idx], name: e.target.value };
                          setEditForm({ ...editForm, additional_contacts: updated });
                        }}
                        className="flex-1 h-8 text-sm"
                      />
                      <Input
                        type="email"
                        placeholder="Email"
                        value={contact.email}
                        onChange={(e) => {
                          const updated = [...editForm.additional_contacts];
                          updated[idx] = { ...updated[idx], email: e.target.value };
                          setEditForm({ ...editForm, additional_contacts: updated });
                        }}
                        className="flex-1 h-8 text-sm"
                      />
                      <Select
                        value={contact.role || "hospital"}
                        onValueChange={(v) => {
                          const updated = [...editForm.additional_contacts];
                          updated[idx] = { ...updated[idx], role: v };
                          setEditForm({ ...editForm, additional_contacts: updated });
                        }}
                      >
                        <SelectTrigger className="w-28 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hospital">Hospital</SelectItem>
                          <SelectItem value="contractor">Contractor</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                        onClick={() => {
                          const updated = editForm.additional_contacts.filter((_, i) => i !== idx);
                          setEditForm({ ...editForm, additional_contacts: updated });
                        }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-500 text-center py-2">No additional contacts added</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateRequest} className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={(open) => {
        setShowDeleteConfirm(open);
        if (!open) {
          setDeletingItem(null);
          setDeletingItems([]);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              {deletingItems.length > 1 ? `Delete ${deletingItems.length} Requests` : "Delete Maintenance Request"}
            </DialogTitle>
            <DialogDescription>
              {deletingItems.length > 1 ? (
                <>Are you sure you want to delete <strong>{deletingItems.length}</strong> maintenance requests? This action cannot be undone.</>
              ) : (
                <>Are you sure you want to delete request <strong>#{deletingItem?.id}</strong>? This action cannot be undone.</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">
              <strong>Warning:</strong> This will permanently delete the maintenance request(s) and all associated data. All tagged users will be notified.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteConfirm(false);
              setDeletingItem(null);
              setDeletingItems([]);
            }}>Cancel</Button>
            <Button onClick={handleDeleteRequest} className="bg-red-600 hover:bg-red-700 text-white">
              <Trash2 className="w-4 h-4 mr-2" />
              {deletingItems.length > 1 ? `Delete ${deletingItems.length} Requests` : "Delete Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notes Modal */}
      <Dialog open={showNotesModal} onOpenChange={setShowNotesModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-amber-600" />
              Notes - Request #{selectedItem?.id}
            </DialogTitle>
            <DialogDescription>
              View and add notes to this maintenance request
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Existing Notes */}
            <div className="space-y-3 max-h-60 overflow-y-auto">
              <p className="text-sm font-medium text-slate-700">Existing Notes</p>
              {selectedItem?.notes && selectedItem.notes.length > 0 ? (
                selectedItem.notes.map((note, idx) => (
                  <div key={idx} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <p className="text-sm text-slate-700">{note.content || note.note || note}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                      {note.created_by_name && <span>by {note.created_by_name}</span>}
                      {note.created_at && <span>• {new Date(note.created_at).toLocaleString()}</span>}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-slate-400 text-sm bg-slate-50 rounded-lg">
                  No notes yet
                </div>
              )}
            </div>

            {/* Add New Note */}
            <div>
              <Label>Add New Note</Label>
              <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Type your note here..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotesModal(false)}>Close</Button>
            <Button onClick={handleAddNote} disabled={!newNote.trim()} className="bg-amber-600 hover:bg-amber-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Log Modal */}
      <Dialog open={showAuditLogModal} onOpenChange={setShowAuditLogModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-purple-600" />
              Activity Log - Request #{selectedItem?.id}
            </DialogTitle>
            <DialogDescription>
              Complete audit trail of all changes and actions
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Request Info Summary */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Type</p>
                  <p className="font-medium">{selectedItem?.request_type || selectedItem?.type || "N/A"}</p>
                </div>
                <div>
                  <p className="text-slate-500">Hospital</p>
                  <p className="font-medium">{selectedItem?.hospital_name || selectedItem?.facility || "N/A"}</p>
                </div>
                <div>
                  <p className="text-slate-500">Created</p>
                  <p className="font-medium">{selectedItem?.created_at ? new Date(selectedItem.created_at).toLocaleDateString() : "N/A"}</p>
                </div>
              </div>
            </div>

            {/* Activity Timeline */}
            <div>
              <p className="text-sm font-medium text-slate-700 mb-3">Activity Timeline</p>
              <div className="space-y-3">
                {selectedItem?.activity && selectedItem.activity.length > 0 ? (
                  [...selectedItem.activity].reverse().map((entry, idx) => (
                    <div key={idx} className="flex gap-3 border-l-2 border-purple-200 pl-4 py-2">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <History className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">
                          {entry.action?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                        </p>
                        <p className="text-sm text-slate-600">{entry.details}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                          {entry.user_name && <span>by {entry.user_name}</span>}
                          {entry.timestamp && <span>• {new Date(entry.timestamp).toLocaleString()}</span>}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-slate-400 text-sm bg-slate-50 rounded-lg">
                    No activity recorded yet
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAuditLogModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Progress Update Modal */}
      <Dialog open={showAddUpdateModal} onOpenChange={setShowAddUpdateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-emerald-600" />
              Add Progress Update
            </DialogTitle>
            <DialogDescription>
              Add a progress update on behalf of the contractor for Request #{updateItem?.id}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-sm text-slate-600"><strong>Work Order:</strong> {updateItem?.work_order_id || "No WO assigned"}</p>
              <p className="text-sm text-slate-600"><strong>Type:</strong> {updateItem?.type || updateItem?.request_type}</p>
              <p className="text-sm text-slate-600"><strong>Current Stage:</strong> {updateItem?.stage}</p>
            </div>

            <div>
              <Label className="text-sm font-medium">Status Update (optional)</Label>
              <Select value={updateStatus} onValueChange={setUpdateStatus}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select status update..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="on_site">Contractor On Site</SelectItem>
                  <SelectItem value="work_in_progress">Work In Progress</SelectItem>
                  <SelectItem value="awaiting_parts">Awaiting Parts</SelectItem>
                  <SelectItem value="parts_received">Parts Received</SelectItem>
                  <SelectItem value="work_completed">Work Completed</SelectItem>
                  <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem value="follow_up_required">Follow-up Required</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-medium">Progress Message *</Label>
              <Textarea 
                value={updateMessage}
                onChange={(e) => setUpdateMessage(e.target.value)}
                placeholder="Describe the progress update, work performed, or any issues encountered..."
                className="mt-1 min-h-[120px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUpdateModal(false)}>Cancel</Button>
            <Button 
              onClick={handleSubmitUpdate} 
              disabled={!updateMessage.trim() || submittingUpdate}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submittingUpdate ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Update
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Contractors & Suppliers Modal */}
      <Dialog open={showManageAssignmentsModal} onOpenChange={setShowManageAssignmentsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-500" />
              Manage Team - {manageAssignmentsItem?.id?.slice(-8)}
            </DialogTitle>
            <DialogDescription>
              Add, edit, or remove contractors and suppliers at any stage
            </DialogDescription>
          </DialogHeader>
          
          {manageAssignmentsItem && (
            <div className="space-y-6">
              {/* Current Contractors */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Wrench className="w-4 h-4" />
                  Assigned Contractors
                </h3>
                <div className="space-y-2">
                  {(manageAssignmentsItem.assigned_contractors || []).length > 0 ? (
                    (manageAssignmentsItem.assigned_contractors || []).map((contractorId, idx) => {
                      const contractor = contractors.find(c => c.id === contractorId);
                      return (
                        <div key={idx} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                          <div>
                            <p className="font-medium text-sm">{contractor?.name || contractor?.company_name || contractorId}</p>
                            <p className="text-xs text-slate-500">{contractor?.email}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            className="h-7"
                            onClick={async () => {
                              try {
                                const res = await fetch(`${API}/admin/maintenance-requests/${manageAssignmentsItem.id}/contractors/${contractorId}`, {
                                  method: 'DELETE',
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                if (res.ok) {
                                  toast.success("Contractor removed");
                                  // Refresh and update local state for dynamic sync
                                  await fetchData();
                                  // Update manageAssignmentsItem to reflect change
                                  setManageAssignmentsItem(prev => ({
                                    ...prev,
                                    assigned_contractors: (prev.assigned_contractors || []).filter(c => c !== contractorId)
                                  }));
                                } else {
                                  toast.error("Failed to remove contractor");
                                }
                              } catch (e) {
                                toast.error("Error removing contractor");
                              }
                            }}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Remove
                          </Button>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-sm text-slate-500 italic">No contractors assigned</p>
                  )}
                </div>
                
                {/* Add Contractor */}
                <div className="mt-3 flex gap-2">
                  <Select
                    onValueChange={async (contractorId) => {
                      try {
                        const contractor = contractors.find(c => c.id === contractorId);
                        const res = await fetch(`${API}/admin/maintenance-requests/${manageAssignmentsItem.id}/assign-contractor`, {
                          method: 'POST',
                          headers: { 
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            contractor_id: contractorId,
                            contractor_name: contractor?.name || contractor?.company_name
                          })
                        });
                        if (res.ok) {
                          toast.success("Contractor added");
                          // Refresh and update local state for dynamic sync
                          await fetchData();
                          // Update manageAssignmentsItem to reflect change
                          setManageAssignmentsItem(prev => ({
                            ...prev,
                            assigned_contractors: [...(prev.assigned_contractors || []), contractorId]
                          }));
                        } else {
                          const data = await res.json();
                          toast.error(data.detail || "Failed to add contractor");
                        }
                      } catch (e) {
                        toast.error("Error adding contractor");
                      }
                    }}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="+ Add Contractor" />
                    </SelectTrigger>
                    <SelectContent>
                      {contractors.filter(c => !(manageAssignmentsItem.assigned_contractors || []).includes(c.id)).map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name || c.company_name} - {c.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Current Suppliers */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Assigned Suppliers
                </h3>
                <div className="space-y-2">
                  {(manageAssignmentsItem.assigned_suppliers || []).length > 0 ? (
                    (manageAssignmentsItem.assigned_suppliers || []).map((supplier, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                        <div>
                          <p className="font-medium text-sm">{supplier.supplier_name || supplier.supplier_id}</p>
                          <p className="text-xs text-slate-500">{supplier.supplier_email} - {supplier.role}</p>
                        </div>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          className="h-7"
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API}/admin/maintenance-requests/${manageAssignmentsItem.id}/suppliers/${supplier.supplier_id}`, {
                                method: 'DELETE',
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              if (res.ok) {
                                toast.success("Supplier removed");
                                // Refresh and update local state for dynamic sync
                                await fetchData();
                                // Update manageAssignmentsItem to reflect change
                                setManageAssignmentsItem(prev => ({
                                  ...prev,
                                  assigned_suppliers: (prev.assigned_suppliers || []).filter(s => s.supplier_id !== supplier.supplier_id)
                                }));
                              } else {
                                toast.error("Failed to remove supplier");
                              }
                            } catch (e) {
                              toast.error("Error removing supplier");
                            }
                          }}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Remove
                        </Button>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 italic">No suppliers assigned</p>
                  )}
                </div>
                
                {/* Add Supplier */}
                <div className="mt-3 flex gap-2">
                  <Select
                    onValueChange={async (supplierId) => {
                      try {
                        const supplier = suppliers.find(s => s.id === supplierId);
                        const res = await fetch(`${API}/admin/maintenance-requests/${manageAssignmentsItem.id}/suppliers`, {
                          method: 'POST',
                          headers: { 
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            supplier_id: supplierId,
                            supplier_name: supplier?.company_name || supplier?.name,
                            supplier_email: supplier?.email,
                            role: "vendor"
                          })
                        });
                        if (res.ok) {
                          toast.success("Supplier added");
                          // Refresh and update local state for dynamic sync
                          await fetchData();
                          // Update manageAssignmentsItem to reflect change
                          setManageAssignmentsItem(prev => ({
                            ...prev,
                            assigned_suppliers: [...(prev.assigned_suppliers || []), {
                              supplier_id: supplierId,
                              supplier_name: supplier?.company_name || supplier?.name,
                              supplier_email: supplier?.email,
                              role: "vendor"
                            }]
                          }));
                        } else {
                          const data = await res.json();
                          toast.error(data.detail || "Failed to add supplier");
                        }
                      } catch (e) {
                        toast.error("Error adding supplier");
                      }
                    }}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="+ Add Supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.filter(s => !(manageAssignmentsItem.assigned_suppliers || []).some(as => as.supplier_id === s.id)).map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.company_name || s.name} - {s.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Audit Log Link */}
              <div className="pt-4 border-t">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="w-full"
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API}/admin/maintenance-requests/${manageAssignmentsItem.id}/audit-log`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      if (res.ok) {
                        const data = await res.json();
                        toast.success(`${data.summary?.total_changes || 0} changes in audit log`);
                      }
                    } catch (e) {
                      toast.error("Failed to fetch audit log");
                    }
                  }}
                >
                  <History className="w-4 h-4 mr-2" />
                  View Audit Log
                </Button>
              </div>

              {/* Add Member by Email - Viral Growth Loop */}
              <div className="pt-4 border-t">
                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-green-600" />
                  Add Member by Email
                </h3>
                <p className="text-xs text-slate-500 mb-3">
                  Add any user by email. If they are not registered, they will receive an Enterprise Onboarding invitation.
                </p>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="user@example.com"
                    value={addMemberEmail}
                    onChange={(e) => setAddMemberEmail(e.target.value)}
                    className="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && addMemberEmail.trim()) {
                        e.preventDefault();
                        handleAddMemberByEmail();
                      }
                    }}
                  />
                  <Button
                    onClick={handleAddMemberByEmail}
                    disabled={!addMemberEmail.trim() || addingMember}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {addingMember ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-1" />
                        Add & Notify
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Show pending invites */}
                {(manageAssignmentsItem.pending_invites || []).length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-amber-600 font-medium mb-1">Pending Invitations:</p>
                    <div className="flex flex-wrap gap-1">
                      {manageAssignmentsItem.pending_invites.map((email, idx) => (
                        <span key={idx} className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {email}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Show tagged users */}
                {(manageAssignmentsItem.tagged_users || []).length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-green-600 font-medium mb-1">Tagged Collaborators:</p>
                    <div className="flex flex-wrap gap-1">
                      {manageAssignmentsItem.tagged_users.map((userId, idx) => (
                        <span key={idx} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          {userId.slice(-8)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManageAssignmentsModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Sync to Contractor Modal */}
      <Dialog open={showSyncModal} onOpenChange={setShowSyncModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Sync to Contractor
            </DialogTitle>
            <DialogDescription>
              Force sync this ticket to a contractor with full notification loop (Email + Telegram)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Ticket Info */}
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Ticket</p>
              <p className="font-bold text-slate-900">#{selectedItem?.id}</p>
              <p className="text-sm text-slate-600">{selectedItem?.hospital_name || selectedItem?.facility}</p>
            </div>
            
            {/* Contractor Email */}
            <div className="space-y-2">
              <Label htmlFor="sync-contractor-email">Contractor Email</Label>
              <Select
                value={syncContractorEmail}
                onValueChange={setSyncContractorEmail}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select contractor..." />
                </SelectTrigger>
                <SelectContent>
                  {contractors.filter(c => c.email).map(c => (
                    <SelectItem key={c.id} value={c.email}>
                      {c.company_name || c.name} ({c.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Or enter email manually:</p>
              <Input
                id="sync-contractor-email"
                type="email"
                placeholder="contractor@company.com"
                value={syncContractorEmail}
                onChange={(e) => setSyncContractorEmail(e.target.value)}
              />
            </div>
            
            {/* Priority */}
            <div className="space-y-2">
              <Label>Priority Level</Label>
              <Select value={syncPriority} onValueChange={setSyncPriority}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="urgent">🔴 Urgent</SelectItem>
                  <SelectItem value="high">🟡 High</SelectItem>
                  <SelectItem value="normal">🟢 Normal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Sync Status */}
            {syncStatus && (
              <div className={`p-4 rounded-lg border ${syncStatus.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                {syncStatus.success ? (
                  <div className="space-y-2">
                    <p className="font-semibold text-green-700 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> Sync Successful!
                    </p>
                    <div className="text-sm text-green-600 space-y-1">
                      <p>✅ Contractor: {syncStatus.contractor?.company_name || syncStatus.contractor?.email}</p>
                      <p>📧 Email: {syncStatus.notifications?.email ? 'Sent' : 'Not sent'}</p>
                      <p>📱 Telegram: {syncStatus.notifications?.telegram ? 'Sent' : (syncStatus.contractor?.telegram_connected ? 'Sent' : 'Not connected')}</p>
                      <p>🔴 Live Thread: {syncStatus.sync_status?.live_thread_active ? 'Active' : 'Inactive'}</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="font-semibold text-red-700 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> Sync Failed
                    </p>
                    <p className="text-sm text-red-600 mt-1">{syncStatus.error}</p>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowSyncModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSyncContractor}
              disabled={syncingContractor || !syncContractorEmail}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {syncingContractor ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Sync Now
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
